import React, { Component } from 'react';
import withStyles, { type WithStyles } from '@material-ui/core/styles/withStyles';
import createStyles from '@material-ui/styles/createStyles';
import format from 'date-fns/format';
import { connect } from 'react-redux';
import {
  Events,
  BlockTypes,
  EnvelopeStatus,
  BlockStatus,
  IS_IOS_CHROME,
  IS_IOS_FIREFOX,
  PostWindowMessageStatus,
} from '../common/constants';
import {
  areGroupRulesValid,
  checkIfSignerHasSettingsSaved,
  getGroupValidationCount,
  handleCalculateFontSize,
  postWindowMessage,
  isSkySlopeMobileApp,
} from '../common/utilities';
// eslint-disable-next-line import/no-named-as-default -- withStyles
import AlreadySignedAlert from '../components/AlreadySignedAlert';
import BottomBar from '../components/BottomBar';
import { CollectPhoneNumberDialogs } from '../components/collect-phone-number/CollectPhoneNumberDialogs';
// eslint-disable-next-line import/no-named-as-default -- withStyles
import Spinner from '../components/common/Spinner';
import Exit from '../components/Exit';
import Header from '../components/Header';
import { PdfViewer } from '../components/pdf/PdfViewer';
import SignatureSettingsModal from '../components/SignatureSettingsModal';
import TermsDialog from '../components/TermsDialog';
import { eventEmitter } from '../index';
import { utcOffset, getTimeZoneShortName } from '../lib/DateUtils';
import saveFile from '../lib/FileSaver';
import { makeInitials } from '../lib/makeInitials';
import { tokenInfo } from '../lib/token';
import type { IRootState } from '../store';
import {
  fetchEnvelope,
  signField,
  zoomIn,
  zoomOut,
  cancelSignatureModal,
  submitEnvelope,
  submitEsignDisclosure,
  showSignatureModal,
  confirmSignatureModal,
  updateBlock,
  tabNextBlock,
  updateSigningExperienceBlock,
  downloadEnvelope,
  signingExperienceComplete,
  showExitSigningModal,
  closeExitSigningModal,
  tabToBlock,
  setPageDimensions,
  setPageScale,
  clearAllPageScales,
  completeBlocksInGroup,
  setCurrentDocument,
  setPendingBlockInfo,
} from '../store/actions';
import type { IBlock, IGroup, ISignatureSettings } from '../store/types';
import { colors } from '../theme';
// Remove for DOP release
// import ThumbnailShelf from '../components/ThumbnailShelf';
import { initChmln } from './chamaleon';

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const styles = () =>
  createStyles({
    '@global': {
      '.ds--container': {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        fontFamily: 'proxima-nova, sans-serif',
        '-webkit-font-smoothing': 'antialiased',
        '-moz-osx-font-smoothing': 'grayscale',
        backgroundColor: colors.background,
      },
      '.ds--header': {
        backgroundColor: colors.paleGrey,
        position: 'fixed',
        left: 0,
        right: 0,
        top: 70,
        textAlign: 'center',
        borderBottom: `1px solid ${colors.paleBlue}`,
        color: colors.dusk,
        fontSize: '14px',
        height: '48px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 5,
      },
      '.ds--header--title': {
        fontWeight: 'bold',
      },
      '.ds--zoom': {
        position: 'absolute',
        bottom: 104,
        right: 24,
      },
      '.ds--hide': {
        display: 'none',
      },
      '.ds--transition': {
        height: '0px',
      },
      '.relative': {
        position: 'relative',
      },
    },
  });

interface ISignerProps extends WithStyles<typeof styles> {
  dispatch: (action: any) => void;
  state: IRootState;
  envelopeId: string;
  signerId: string;
  showSuccessScreen: boolean;
}

interface IState {
  requiredGroups: IGroup[];
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const getEnvelopeStatus = (isMeSigner: boolean): 'sentWithSignNow' | 'sent' => {
  return isMeSigner ? 'sentWithSignNow' : 'sent';
};

export class Signer extends Component<ISignerProps, IState> {
  constructor(props: ISignerProps) {
    super(props);
    this.state = {
      requiredGroups: [],
    };
    this.onUnload = this.onUnload.bind(this);
  }

  componentDidMount() {
    window.mobileApp = {
      backButtonTap: () => {}, // Provide a default implementation once available
      finishSigning: this.submitEnvelope.bind(this),
      editSignature: this.editSignature.bind(this),
      acceptTerms: this.acceptTerms.bind(this),
      clickAndSign: this.clickAndSign.bind(this),
    };
    window.addEventListener('beforeunload', this.onUnload);
    window.addEventListener('pagehide', this.onUnload);
    this.props.dispatch(fetchEnvelope(this.props.envelopeId, this.props.signerId));
    window.addEventListener('keydown', (e) => {
      const { modal, signingExperience } = this.props.state;
      if ((e.key === 'Tab' || e.key === 'Enter') && !modal.showSignatureModal && modal.termsAccepted) {
        e.preventDefault();
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        const block = signingExperience.sortedBlocks[signingExperience.currentSelectedBlockIndex];
        if (e.key === 'Enter' && block && [BlockTypes.SIGNATURE, BlockTypes.INITIALS].includes(block.blockType)) {
          if (!modal.signatureAccepted) {
            this.props.dispatch(showSignatureModal(block.documentId, block.pageNumber, block.blockId));
            postWindowMessage({
              header_text: 'Signature',
              back_button_type: '<',
              should_back_button_exit: false,
              button_text: 'Next',
              digi_mobile_status: PostWindowMessageStatus.SET_SIGNATURE_WINDOW_OPEN,
              metadata: {
                envelope_status: getEnvelopeStatus(isMeSigner),
              },
            });
            return;
          }
          this.props.dispatch(signField(block.documentId, block.pageNumber, block.blockId));
        }
        if (
          (this.requiredFieldsTotal !== this.requiredFieldsSigned || !signingExperience.completedPassThrough) &&
          this.props.state.envelope.envelope!.status !== EnvelopeStatus.COMPLETE
        ) {
          this.scrollToNextBlock(this.props.state.signingExperience.currentSelectedBlockIndex || undefined);
        }
      }
    });
    const isMeSigner = this.props.state.envelope.envelope?.signer.title === 'Me';
    postWindowMessage({
      header_text: 'Documents to Sign',
      back_button_type: '<',
      should_back_button_exit: false,
      button_text: 'Start Signing',
      digi_mobile_status: PostWindowMessageStatus.DIGISIGN_PREPARE_FOR_SIGNATURE,
      metadata: {
        envelope_status: getEnvelopeStatus(isMeSigner),
      },
    });
  }

  // @ts-expect-error -- This code was written prior to enforcing lint rules
  shouldComponentUpdate(nextProps: ISignerProps, _state: IRootState) {
    if (nextProps.state.modal) {
      if (nextProps.state.modal.alreadySigned) {
        this.removeEventListener();
      }
    }
    if (this.props.state.blocks && this.props.state.envelope.envelope) {
      if (
        this.requiredFieldsSigned === this.requiredFieldsTotal &&
        this.props.state.signingExperience.completedPassThrough
      ) {
        this.scrollToNextBlock(this.props.state.signingExperience.currentSelectedBlockIndex || undefined);
      }
    }
    return true;
  }

  addEventListener = () => {
    window.addEventListener('beforeunload', this.onUnload);
  };

  removeEventListener = () => {
    window.removeEventListener('beforeunload', this.onUnload);
  };

  onUnload() {
    this.tearDownCanvas();
  }

  get requiredFieldsSigned() {
    // TODO: this gets called twice each render, fix that
    const { blocks, groups } = this.props.state;
    let fieldsSigned = 0;
    const groupsChecked: { [key: string]: boolean } = {};
    Object.keys(blocks).forEach((documentID) => {
      Object.keys(blocks[documentID]).forEach((pageNumber) => {
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        Object.keys(blocks[documentID][pageNumber]).forEach((blockId) => {
          // @ts-expect-error -- This code was written prior to enforcing lint rules
          const currentBlock: IBlock = blocks[documentID][pageNumber][blockId];
          if (currentBlock.groupId && !groupsChecked[currentBlock.groupId!]) {
            // @ts-expect-error -- This code was written prior to enforcing lint rules
            if (groups[documentID][pageNumber]) {
              // @ts-expect-error -- This code was written prior to enforcing lint rules
              const currentGroup = groups[documentID][pageNumber][currentBlock.groupId!];
              if (
                currentGroup.validation.rule === 'lte' ||
                (currentGroup.validation.rule === 'gte' && currentGroup.validation.value === 0)
              ) {
                groupsChecked[currentBlock.groupId] = true;
              } else if (
                currentBlock.blockType !== BlockTypes.DATE_SIGNED &&
                currentBlock.status === BlockStatus.COMPLETE &&
                areGroupRulesValid(currentBlock, currentGroup, blocks) &&
                this.state.requiredGroups.some((g) => g.id === currentBlock.groupId)
              ) {
                groupsChecked[currentBlock.groupId] = true;
                fieldsSigned += 1;
              }
            }
          } else if (
            currentBlock.required &&
            currentBlock.blockType !== BlockTypes.DATE_SIGNED &&
            currentBlock.status === BlockStatus.COMPLETE &&
            (currentBlock.blockType === BlockTypes.CHECKBOX && !!currentBlock.blockId ? false : true)
          ) {
            fieldsSigned += 1;
          }
        });
      });
    });
    return fieldsSigned;
  }

  get requiredFieldsTotal() {
    const { groups } = this.props.state;
    const blockTotal = this.props.state.envelope.envelope!.documents.reduce((total, doc) => {
      return (
        total +
        doc.blocks.filter(
          (block) =>
            block.required &&
            block.blockType !== BlockTypes.DATE_SIGNED &&
            (block.blockType === BlockTypes.CHECKBOX && !!block.blockId ? false : true)
        ).length
      );
    }, 0);
    let groupTotal = 0;
    Object.keys(groups).forEach((documentId) => {
      Object.keys(groups[documentId]).forEach((pageIndex) => {
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        groupTotal += Object.values(groups[documentId][pageIndex]).filter((group: IGroup) => {
          let currentValidationCount = 0;
          if (this.props.state.envelope.envelope?.signer.signingStatus !== 'Complete') {
            currentValidationCount = getGroupValidationCount(
              documentId,
              parseInt(pageIndex),
              group,
              this.props.state.blocks
            );
          }
          if (group.validation.rule === 'gte' && group.validation.value === 0) {
            return false;
          }
          if (
            group.validation.rule === 'gte' &&
            currentValidationCount >= group.validation.value &&
            !this.state.requiredGroups.some((g) => g.id === group.id)
          ) {
            return false;
          }
          if (
            group.validation.rule === 'eq' &&
            group.validation.value === currentValidationCount &&
            !this.state.requiredGroups.some((g) => g.id === group.id)
          ) {
            return false;
          }
          if (group.validation.rule === 'lte') {
            return false;
          }
          if (!this.state.requiredGroups.some((g) => g.id === group.id)) {
            this.state.requiredGroups.push(group);
          }
          return true;
        }).length;
      });
    });
    return blockTotal + groupTotal;
  }

  get completed() {
    if (this.requiredFieldsSigned === this.requiredFieldsTotal) {
      eventEmitter.emit(Events.ENVELOPE_COMPLETED);
      return true;
    }
    return false;
  }

  zoomIn = () => this.props.dispatch(zoomIn());
  zoomOut = () => this.props.dispatch(zoomOut());

  cancelSignatureModal = () => this.props.dispatch(cancelSignatureModal());
  confirmSignatureModal = (signatureSettings: ISignatureSettings, useExistingSigningSettings?: boolean) => {
    let currentDocumentId;
    let currentPageNumber;
    let currentBlockId;
    const { state } = this.props;
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const block = state.signingExperience.sortedBlocks[state.signingExperience.currentSelectedBlockIndex];
    const { envelopeId, signer } = this.props.state.envelope.envelope!;
    if (!state.modal.pendingBlockInfo && state.modal.getBlockValueFromSettings) {
      currentDocumentId = block.documentId;
      currentPageNumber = block.pageNumber;
      currentBlockId = block.blockId;
    } else if (state.modal.pendingBlockInfo) {
      const { documentId, pageNumber, blockId } = state.modal.pendingBlockInfo!;
      currentDocumentId = documentId;
      currentPageNumber = pageNumber;
      currentBlockId = blockId;
    }
    const { currentSelectedBlockIndex } = state.signingExperience;
    this.props.dispatch(
      confirmSignatureModal(
        envelopeId,
        currentDocumentId,
        currentPageNumber,
        currentBlockId,
        signer.signerId,
        signatureSettings,
        useExistingSigningSettings
      )
    );
    if (currentSelectedBlockIndex !== null) {
      this.props.dispatch(updateSigningExperienceBlock(currentSelectedBlockIndex));
    }
  };

  acceptTerms = () => {
    const { envelopeId, signer } = this.props.state.envelope.envelope!;
    this.props.dispatch(submitEsignDisclosure(envelopeId, signer.signerId));
  };

  submitEnvelope = () => {
    const documents = this.props.state.blocks;
    const signatureSettings = this.props.state.modal.signatureSettings!;
    const body: any = {
      documents: [],
      localeTimeZoneOffset: utcOffset(),
      timeZoneShortName: getTimeZoneShortName(),
      dateFormat: this.props.state.envelope.envelope!.config.dateFormat,
    };

    for (let index in documents) {
      let blocks = Object.keys(documents[index])
        .map((pageNumber) =>
          // @ts-expect-error -- This code was written prior to enforcing lint rules
          Object.keys(documents[index][pageNumber]).map((blockId) => ({ ...documents[index][pageNumber][blockId] }))
        )
        .flat();
      let filteredBlocks: IBlock[] = blocks.filter((block) => block.status === BlockStatus.COMPLETE);
      filteredBlocks.forEach((block) => {
        if (block.blockType === BlockTypes.SIGNATURE) {
          block.value = signatureSettings.signatureType === 'text' ? signatureSettings.signatureValue : 'image';
        } else if (block.blockType === BlockTypes.INITIALS) {
          block.value = signatureSettings.signatureType === 'text' ? signatureSettings.initialsValue : 'image';
          // @ts-expect-error -- This code was written prior to enforcing lint rules
        } else if (block.blockType === BlockTypes.TEXT && !block.isEditingDisabled) {
          const fontSize = handleCalculateFontSize(
            block.value as string,
            // @ts-expect-error -- This code was written prior to enforcing lint rules
            block.fontSize,
            block.height,
            block.width,
            this.props.state.zoom
          );
          if (block.fontSize !== fontSize) {
            block = { ...block, fontSize };
          }
        }
      });

      body.documents.push({
        documentId: index,
        blocks: filteredBlocks,
      });
    }
    this.props.dispatch(
      submitEnvelope(
        this.props.envelopeId,
        this.props.signerId,
        body,
        signatureSettings?.signatureFont,
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        this.props.state.envelope.envelope.signer
      )
    );
    const isMeSigner = this.props.state.envelope.envelope?.signer.title === 'Me';
    postWindowMessage({
      header_text: 'Documents to Sign',
      back_button_type: '<',
      should_back_button_exit: false,
      button_text: 'Finish Signing',
      digi_mobile_status: PostWindowMessageStatus.SIGNING_COMPLETE,
      metadata: {
        envelope_status: getEnvelopeStatus(isMeSigner),
        totalSignatureBlocks: this.requiredFieldsTotal.toString(),
        currentBlocksSigned: this.requiredFieldsSigned.toString(),
      },
    });
  };

  exitSigning = () => {
    const { areLegalLinksVisible } = this.props.state.envelope.envelope!.config;

    if (areLegalLinksVisible) {
      this.handleShowConfirmationPage();
    } else {
      eventEmitter.emit(Events.EXIT_SIGNING, {
        envelopeId: this.props.envelopeId,
        status: this.props.state.envelope.envelope!.status,
      });
      this.props.dispatch(closeExitSigningModal());
    }
  };

  handleShowExitSigningModal = () => {
    this.props.dispatch(showExitSigningModal());
  };

  handleCloseExitSigningModal = () => {
    this.props.dispatch(closeExitSigningModal());
  };

  tearDownCanvas = () => {
    let pdfs = document.querySelectorAll('canvas');
    pdfs.forEach((pdf) => {
      let context = pdf.getContext('2d');
      pdf.height = 0;
      pdf.width = 0;
      context?.clearRect(0, 0, pdf.width, pdf.height);
    });
  };

  handleShowConfirmationPage = () => {
    this.tearDownCanvas();
    this.props.dispatch(closeExitSigningModal());
    eventEmitter.emit(Events.EXIT_SIGNING_EXPERIENCE);
  };

  scrollToPage = (fullPage: number) => {
    const e = document.getElementById(`page-${fullPage}`);
    window.focus();
    document.querySelector('.ds--content')!.scrollTop = e!.offsetTop;
  };

  updateBlock = (documentId: string, pageNumber: number, blockId: string, blockUpdate: any) => {
    this.props.dispatch(updateBlock(documentId, pageNumber, blockId, blockUpdate));
  };

  clickAndSign = () => {
    const { currentSelectedBlockIndex, sortedBlocks } = this.props.state.signingExperience;
    const currentBlockIndex = currentSelectedBlockIndex ?? 0;
    const block = sortedBlocks[currentBlockIndex];
    this.clickSignature(block.documentId, block.pageNumber, block.blockId, currentBlockIndex);
  };

  clickSignature = (documentId: string, pageNumber: number, blockId: string, tabIndex?: number) => {
    const signatureAccepted = this.props.state.modal.signatureAccepted;
    const isMeSigner = this.props.state.envelope.envelope?.signer.title === 'Me';
    const signer = this.props.state.envelope.envelope?.signer;
    const defaultName = [signer?.firstName, signer?.middleName, signer?.lastName].filter(Boolean).join(' ');
    this.tabToBlock(tabIndex!);
    eventEmitter.emit(Events.BLOCK_CLICKED);
    if (!isMeSigner) {
      postWindowMessage({
        header_text: 'Documents to Sign',
        back_button_type: '<',
        should_back_button_exit: false,
        button_text: 'Sign & Next',
        metadata: {
          envelope_status: 'sent',
        },
      });
    }
    if (isMeSigner && checkIfSignerHasSettingsSaved(signer?.signingSettings) && !signatureAccepted) {
      this.props.dispatch(setPendingBlockInfo(documentId, pageNumber, blockId));
      if (signer?.signingSettings?.signatureType === 'text') {
        this.confirmSignatureModal(
          {
            signatureValue: signer?.signingSettings.signatureValue ?? defaultName,
            initialsValue: signer?.signingSettings.initialsValue ?? makeInitials(defaultName),
            saveSigningSettings: signer.signingSettings?.saveSigningSettings,
            signatureType: 'text',
            signatureFont: signer.signingSettings?.signatureFont,
          },
          true
        );
        this.props.dispatch(signField(documentId, pageNumber, blockId));
        this.scrollToNextBlock(tabIndex);
      } else {
        this.confirmSignatureModal(
          {
            signatureValue: signer?.signingSettings?.signatureValue,
            initialsValue: signer?.signingSettings?.initialsValue,
            saveSigningSettings: signer?.signingSettings?.saveSigningSettings,
            signatureType: 'image',
          },
          true
        );
        this.props.dispatch(signField(documentId, pageNumber, blockId));
        this.scrollToNextBlock(tabIndex);
      }
    } else if (!signatureAccepted) {
      postWindowMessage({
        header_text: 'Signature',
        back_button_type: '<',
        should_back_button_exit: false,
        button_text: 'Next',
        digi_mobile_status: PostWindowMessageStatus.SET_SIGNATURE_INCOMPLETE,
        metadata: {
          envelope_status: getEnvelopeStatus(isMeSigner),
        },
      });
      this.props.dispatch(showSignatureModal(documentId, pageNumber, blockId));
      postWindowMessage({
        header_text: 'Signature',
        back_button_type: '<',
        should_back_button_exit: false,
        button_text: 'Next',
        digi_mobile_status: PostWindowMessageStatus.SET_SIGNATURE_WINDOW_OPEN,
        metadata: {
          envelope_status: getEnvelopeStatus(isMeSigner),
        },
      });
    } else {
      this.props.dispatch(signField(documentId, pageNumber, blockId));
      this.scrollToNextBlock(tabIndex);
    }
  };

  isPassThroughComplete = (currentIndex: number, blocks: IBlock[]) => {
    const isMeSigner = this.props.state.envelope.envelope?.signer.title === 'Me';
    if (currentIndex !== blocks.length - 1) {
      postWindowMessage({
        header_text: 'Documents to Sign',
        back_button_type: '<',
        should_back_button_exit: false,
        button_text: 'Sign & Next',
        digi_mobile_status: PostWindowMessageStatus.SIGNING_IN_PROGRESS,
        metadata: {
          envelope_status: getEnvelopeStatus(isMeSigner),
          totalSignatureBlocks: (blocks.length - 1).toString(),
          currentBlocksSigned: currentIndex.toString(),
        },
      });
    }
    return currentIndex === blocks.length - 1;
  };

  editSignature = () => {
    this.props.dispatch(showSignatureModal());
    postWindowMessage({
      header_text: 'Signature',
      back_button_type: '<',
      should_back_button_exit: false,
      button_text: 'Next',
      digi_mobile_status: PostWindowMessageStatus.SET_SIGNATURE_WINDOW_OPEN,
    });
  };

  scrollToNextBlock = (tabIndex?: number) => {
    const temp = tabIndex !== undefined && tabIndex !== 0 ? tabIndex - 1 : undefined;
    const { currentSelectedBlockIndex, sortedBlocks } = this.props.state.signingExperience;
    if (currentSelectedBlockIndex !== null && sortedBlocks[currentSelectedBlockIndex].groupId) {
      const currentBlock = sortedBlocks[currentSelectedBlockIndex];
      const groups = sortedBlocks.filter((block) => block.groupId === currentBlock.groupId);
      if (this.isPassThroughComplete(sortedBlocks.indexOf(groups[groups.length - 1]), sortedBlocks)) {
        this.props.dispatch(signingExperienceComplete());
      }
    }
    if (currentSelectedBlockIndex !== null && this.isPassThroughComplete(currentSelectedBlockIndex, sortedBlocks)) {
      this.props.dispatch(signingExperienceComplete());
    }
    this.props.dispatch(
      tabNextBlock(
        temp !== undefined && !currentSelectedBlockIndex ? temp : currentSelectedBlockIndex,
        this.props.state.blocks,
        this.props.state.groups,
        tabIndex
      )
    );
    if (this.requiredFieldsSigned === this.requiredFieldsTotal) {
      postWindowMessage({
        header_text: 'Documents to Sign',
        back_button_type: '<',
        should_back_button_exit: false,
        button_text: 'Finish Signing',
        digi_mobile_status: PostWindowMessageStatus.SIGNING_IN_PROGRESS,
        metadata: {
          totalSignatureBlocks: this.requiredFieldsTotal.toString(),
          currentBlocksSigned: this.requiredFieldsSigned.toString(),
        },
      });
    }
  };

  //Uncomment this function when ready to implement on mobile to enable previous block navigation
  // previousBlock = (tabIndex?: number) => {
  //   if (tabIndex !== undefined && tabIndex > 0) {
  //     const previousIndex = tabIndex - 1;
  //     // Scroll to the previous block
  //     this.scrollToNextBlock(previousIndex);
  //   } else {
  //     console.warn('No previous blocks to navigate to.');
  //   }
  // };

  tabToBlock = (nextIndex: number) => {
    this.props.dispatch(tabToBlock(nextIndex));
  };

  getRequiredPendingBlockIds = () => {
    let blocksByPage = {};
    Object.keys(this.props.state.blocks).forEach((key) => {
      Object.keys(this.props.state.blocks[key]).forEach((block) => {
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        const currentBlock = this.props.state.blocks[key][block];
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        if (!blocksByPage[currentBlock.pageNumber]) {
          // @ts-expect-error -- This code was written prior to enforcing lint rules
          blocksByPage[currentBlock.pageNumber] = [];
        }
        if (
          currentBlock.required &&
          currentBlock.blockType !== BlockTypes.DATE_SIGNED &&
          currentBlock.status === BlockStatus.PENDING
        ) {
          // @ts-expect-error -- This code was written prior to enforcing lint rules
          blocksByPage[currentBlock.pageNumber].push(currentBlock);
        }
      });
    });
    return blocksByPage;
  };

  getRequiredCompleteBlockIds = () => {
    let blocksByPage = {};
    Object.keys(this.props.state.blocks).forEach((key) => {
      Object.keys(this.props.state.blocks[key]).forEach((block) => {
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        const currentBlock = this.props.state.blocks[key][block];
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        if (!blocksByPage[currentBlock.pageNumber]) {
          // @ts-expect-error -- This code was written prior to enforcing lint rules
          blocksByPage[currentBlock.pageNumber] = [];
        }
        if (
          currentBlock.required &&
          currentBlock.blockType !== BlockTypes.DATE_SIGNED &&
          currentBlock.status === BlockStatus.COMPLETE &&
          currentBlock.blockType === BlockTypes.CHECKBOX &&
          currentBlock.groupId
        ) {
          // @ts-expect-error -- This code was written prior to enforcing lint rules
          blocksByPage[currentBlock.pageNumber].push(currentBlock);
        }
      });
    });
    return blocksByPage;
  };

  downloadEnvelopeFile = (downloadType: string) => {
    const { downloadedFile } = this.props.state.envelope;
    const { alreadySigned } = this.props.state.modal;
    const { envelopeId, signer, documents } = this.props.state.envelope.envelope!;
    let downloadFileName;
    if (documents.length > 1) {
      downloadFileName = `digisign-documents-${format(new Date(), 'MM-DD-YYYY')}.${downloadType}`;
    } else {
      downloadFileName = documents[0].fileName;
    }

    if (
      downloadedFile.alreadySigned === alreadySigned &&
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      downloadedFile[downloadType] &&
      (IS_IOS_CHROME || IS_IOS_FIREFOX)
    ) {
      this.props.dispatch(downloadEnvelope(envelopeId, signer.signerId, downloadFileName, alreadySigned, downloadType));
      return;
      // @ts-expect-error -- This code was written prior to enforcing lint rules
    } else if (downloadedFile.alreadySigned === alreadySigned && downloadedFile[downloadType]) {
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      saveFile(downloadedFile[downloadType].blobUrl, downloadedFile[downloadType].fileName, downloadedFile);
      return;
    }

    this.props.dispatch(downloadEnvelope(envelopeId, signer.signerId, downloadFileName, alreadySigned, downloadType));
  };

  handlePageLoaded = (page: any, fullPage: number) => {
    const { state } = this.props;
    const { pageDimensions } = state.envelope;
    if (!pageDimensions[fullPage]) {
      this.props.dispatch(
        setPageDimensions(page.height / this.props.state.zoom, page.width / this.props.state.zoom, fullPage)
      );
    }
  };

  setPageScale = (fullPage: number, pageScale: number) => {
    this.props.dispatch(setPageScale(fullPage, pageScale));
  };

  clearAllPageScales = () => {
    this.props.dispatch(clearAllPageScales());
  };

  setBlocksInGroupToComplete = (blocks: IBlock[]) => {
    this.props.dispatch(completeBlocksInGroup(blocks));
  };

  handleSetCurrentDocument = (documentId: string, documentName: string) => {
    this.props.dispatch(setCurrentDocument(documentId, documentName));
  };

  render() {
    const { state, showSuccessScreen } = this.props;
    const envelope = state.envelope.envelope;
    let documents: any = [];
    const isMobile = isSkySlopeMobileApp();
    const signer = envelope?.signer;
    const isMeSigner = signer?.title === 'Me';
    const oktaUserId = signer?.oktaUserId;
    if (isMeSigner && oktaUserId && !(window as any).chmln) {
      initChmln();
      const isSignatureSaved = !!checkIfSignerHasSettingsSaved(signer?.signingSettings);
      (window as any).chmln?.identify(oktaUserId, { isSignatureSaved: isSignatureSaved });
    }

    if (
      !envelope ||
      state.envelope.isSubmitting ||
      (!showSuccessScreen && envelope.status === EnvelopeStatus.COMPLETE)
    ) {
      return (
        <div className="ds--container">
          <Spinner />
        </div>
      );
    }
    documents = envelope.documents;
    if (envelope.signer.executedDocuments.length > 0) {
      documents = envelope.signer.executedDocuments;
      envelope.signer.executedDocuments.forEach((_, i) => {
        documents[i].numberOfPages = envelope!.documents[i].numberOfPages;
      });
    }

    const currentDocument = envelope.documents.findIndex((d) => d.documentId === state.envelope.currentDocumentId);
    // Remove for DOP release
    // const numberOfRequiredCompleteBlocks = this.getRequiredCompleteBlockIds();
    // const numberOfRequiredPendingBlocks = this.getRequiredPendingBlockIds();
    return (
      <div className="ds--container">
        <Header
          numberOfDocuments={envelope.documents.length}
          currentDocumentIndex={currentDocument}
          fileName={state.envelope.currentDocumentName}
          zoom={state.zoom}
          zoomIn={this.zoomIn}
          zoomOut={this.zoomOut}
        />
        {/* Remove for DOP release
        <ThumbnailShelf
          documents={envelope.documents}
          unsignedBlocksByPage={numberOfRequiredPendingBlocks}
          signedBlocksByPage={numberOfRequiredCompleteBlocks}
          completed={this.completed}
          scrollToPage={this.scrollToPage}
        />
        */}
        <PdfViewer
          envelope={state.envelope}
          documents={documents}
          zoom={state.zoom}
          blocks={state.blocks}
          updateBlock={this.updateBlock}
          clickSignature={this.clickSignature}
          groups={state.groups}
          signingExperienceBlocks={state.signingExperience.sortedBlocks}
          signingExperienceComplete={state.signingExperience.completedPassThrough}
          currentSelectedBlockIndex={state.signingExperience.currentSelectedBlockIndex}
          scrollToNextBlock={this.scrollToNextBlock}
          tabToBlock={this.tabToBlock}
          allBlocksComplete={this.requiredFieldsSigned === this.requiredFieldsTotal}
          onPageLoad={this.handlePageLoaded}
          pageDimensions={state.envelope.pageDimensions}
          pageScale={state.envelope.pageScale}
          setPageScale={this.setPageScale}
          clearAllPageScales={this.clearAllPageScales}
          signatureSettings={state.modal.signatureSettings}
          setBlocksInGroupToComplete={this.setBlocksInGroupToComplete}
          setCurrentDocument={this.handleSetCurrentDocument}
        />
        <SignatureSettingsModal
          show={state.modal.showSignatureModal && !state.modal.alreadySigned}
          cancelSignatureModal={this.cancelSignatureModal}
          confirmSignatureModal={this.confirmSignatureModal}
          scrollToNextBlock={this.scrollToNextBlock}
          signatureAccepted={state.modal.signatureAccepted}
          alreadySigned={state.modal.alreadySigned}
          signer={envelope!.signer}
        />
        <TermsDialog
          config={envelope.config}
          accepted={state.modal.termsAccepted}
          acceptTerms={this.acceptTerms}
          visible={isMobile ? false : !state.modal.termsAccepted}
          exitSigning={this.exitSigning}
          loading={state.envelope.requestPending}
        />
        <CollectPhoneNumberDialogs
          isOpen={Boolean(
            envelope.signer.metadata?.audienceContactType === 'Buyer' &&
              !state.modal.alreadySigned &&
              state.modal.termsAccepted &&
              // Tag from Forms document
              envelope.documents.some((d) => d.metadata.collectAllSignerPhoneNumbers) &&
              !state.modal.signerPhoneNumber
          )}
          envelopeId={this.props.envelopeId}
          signerId={this.props.signerId}
          signerEmail={tokenInfo.Email}
        />

        <Exit
          showExitSigningModal={state.modal.showExitSigningModal}
          closeModal={this.handleCloseExitSigningModal}
          showConfirmationPage={this.exitSigning}
        />
        {state.modal.alreadySigned && (
          <AlreadySignedAlert
            envelopeId={envelope.envelopeId}
            documents={envelope.documents}
            dateSigned={envelope.signer.dateSigned}
            downloadEnvelopeFile={this.downloadEnvelopeFile}
            redirectUrl={envelope.config.senderUi.sendRedirectUrl}
          />
        )}
        {!isMobile && (
          <BottomBar
            requiredFieldsSigned={this.requiredFieldsSigned}
            requiredFieldsTotal={this.requiredFieldsTotal}
            submitEnvelope={this.submitEnvelope}
            exitSigning={this.handleShowExitSigningModal}
            completed={this.completed}
            alreadySigned={state.modal.alreadySigned}
            termsAccepted={state.modal.termsAccepted}
            signingActionsMenuOpen={state.envelope.signingActionsMenuOpen}
            documents={envelope!.documents}
            addEventListener={this.addEventListener}
            removeEventListener={this.removeEventListener}
            envelopeId={envelope!.envelopeId}
            downloadEnvelopeFile={this.downloadEnvelopeFile}
            config={envelope.config}
            scrollToNextBlock={this.scrollToNextBlock}
            currentBlockIndex={this.props.state.signingExperience.currentSelectedBlockIndex}
            signer={envelope?.signer}
            showEditSignatureModal={() => this.props.dispatch(showSignatureModal())}
            signatureAccepted={state.modal.signatureAccepted}
          />
        )}
      </div>
    );
  }
}

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default connect((state: IRootState) => ({ state }))(withStyles(styles)(Signer));
